import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Container,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  CircularProgress,
  Chip,
  Tooltip
} from "@mui/material";
import { ExpandMore, Schedule, CalendarToday, InfoOutlined } from "@mui/icons-material";
import { useQuery } from "@apollo/client";
import { useSnackbar } from "notistack";
import StudentNavBar from "app/components/StudentNavBar";
import { colors } from "styles/colors";
import images from "assets/images/images";
import { GET_MY_SERVICE_PURCHASES } from "app/graphql/services";
import ConsultationBookingModal from "./ConsultationBookingModal";
import { getUserInfo } from "utils/helpers";
import { clarityEvent } from "utils/hooks/useClarity";

interface ServicePurchase {
  id: string;
  service_name: string;
  provider_name: string;
  duration_minutes: number;
  purchased_date_formatted: string;
  expires_date_formatted: string;
  purchased_price_formatted: string;
  is_active_and_valid: boolean;
  is_expired: boolean;
  days_until_expiry: number;
  has_external_session: boolean;
  status: string;
  user_purchase?: {
    id: string;
    transaction_id: string;
    total_amount: number;
    purchased_at: string;
    payment_status: string;
  };
}

const StudentSelfcareComponent = () => {
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const [purchasedSessionsExpanded, setPurchasedSessionsExpanded] = useState(true);
  
  // Scroll to top when component mounts
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Get user info from localStorage
  const userInfo = getUserInfo();

  // Fetch previous purchases
  const { data: purchasesData, loading: purchasesLoading } = useQuery(GET_MY_SERVICE_PURCHASES, {
    variables: {
      filter: { identifier: "clovemind" },
      status: "active",
      limit: 10,
      offset: 0
    },
    fetchPolicy: "cache-and-network",
    errorPolicy: "all",
    onError: (error) => {
      console.error("Error fetching service purchases:", error);
    }
  });

  const servicePurchases: ServicePurchase[] = purchasesData?.service_purchases || [];
  const hasPurchases = servicePurchases.length > 0;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const hasActiveSessions = servicePurchases.some(p => p.is_active_and_valid);

  // Track page view on mount
  React.useEffect(() => {
    clarityEvent("selfcare_page_viewed", {
      userId: userInfo.id,
      hasPurchases: hasPurchases ? "true" : "false",
      purchaseCount: servicePurchases.length,
      hasActiveSessions: hasActiveSessions ? "true" : "false"
    });
  }, []); // Only fire once on mount

  const handleBookNowClick = () => {
    clarityEvent("selfcare_book_now_clicked", {
      userId: userInfo.id,
      serviceName: servicePurchases[0]?.service_name || "1:1 Virtual Counselling Session",
      source: "selfcare_landing"
    });
    history.push('/student-selfcare-details');
  };

  const handleScheduleSession = (purchase: ServicePurchase) => {
    if (purchase.status === 'expired' || purchase.is_expired) {
      clarityEvent("selfcare_expired_session_attempt", {
        userId: userInfo.id,
        sessionId: purchase.id
      });
      enqueueSnackbar("This session has expired. Please purchase a new session.", { variant: "warning" });
      return;
    }

    clarityEvent("selfcare_schedule_session_clicked", {
      userId: userInfo.id,
      sessionId: purchase.id,
      daysUntilExpiry: purchase.days_until_expiry
    });

    // For now, show a message. In future, this could open a booking modal or redirect to external booking
    // enqueueSnackbar("Booking instructions have been sent to your email", { variant: "info" });
    setIsModalOpen(true);
  };

  const formatStatus = (status: string) => {
    if (!status) return '';
    // Split by underscore and capitalize each word
    return status
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const getStatusChip = (purchase: ServicePurchase) => {
    if (purchase.is_expired) {
      return <Chip label="Expired" size="small" sx={{ bgcolor: '#FFEBEE', color: '#D32F2F' }} />;
    }
    if (purchase.days_until_expiry <= 7) {
      return <Chip label={`Expires in ${purchase.days_until_expiry} days`} size="small" sx={{ bgcolor: '#FFF8E1', color: '#F57C00' }} />;
    }
    if (purchase.is_active_and_valid) {
      return <Chip label="Active" size="small" sx={{ bgcolor: '#E8F5E9', color: '#2E7D32' }} />;
    }
    return null;
  };

  return (
    <Box sx={{ bgcolor: '#F9FAFB', minHeight: '100vh' }}>
      {/* Main Content */}
      <Box sx={{ 
        px: 0,
        pb: 5 }}
      >
        {/* Hero Section */}
        <Box
          sx={{
            backgroundColor: '#FB8566',
            py: { xs: 3, sm: 2.5, md: 2 },
            px: { xs: 2, sm: 3, md: 4 },
            borderRadius: '10px',
            position: 'relative',
            overflow: 'visible',
            mb: 4
          }}
        >
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' }
          }}>
            {/* Mobile Layout */}
            <Box sx={{ 
              display: { xs: 'flex', sm: 'none' },
              width: '100%'
            }}>
              {/* Mobile: Image and Title Side by Side */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'flex-start',
                gap: '16px',
                mb: 1.5
              }}>
                {/* Character Image - Mobile */}
                <Box
                  sx={{
                    width: 87,
                    height: 87,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                >
                  <Box
                    component="img"
                    src={images?.cloveProfile}
                    alt="cloveProfile"
                    sx={{
                      width: '87px',
                      height: '87px',
                      objectFit: 'contain'
                    }}
                  />
                </Box>
                {/* Main Title - Mobile */}
                <Typography
                  sx={{
                    color: 'white',
                    fontWeight: 500,
                    fontSize: '18px',
                    lineHeight: 1.3
                  }}
                >
                  Deadlines. Exam Stress. Career Choices. Loneliness. Relationships.
                </Typography>
              </Box>
            </Box>
            
            {/* Mobile: Subtitle Below */}
            <Box sx={{ 
              display: { xs: 'block', sm: 'none' },
              width: '100%'
            }}>
              <Typography
                sx={{
                  color: 'white',
                  fontSize: '14px',
                  lineHeight: 1.5
                }}
              >
                College life isn&apos;t always easy. And that&apos;s okay! We are here to support and be a part of your emotional well-being.
              </Typography>
            </Box>

            {/* Desktop Layout */}
            <Box sx={{ 
              display: { xs: 'none', sm: 'flex' },
              alignItems: 'center',
              width: '100%'
            }}>
              {/* Character Image - Desktop */}
              <Box
                sx={{
                  width: { sm: 90, md: 100 },
                  height: { sm: 90, md: 100 },
                  bgcolor: 'rgba(255,255,255,0.2)',
                  borderRadius: '10px',
                  mr: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Box
                  component="img"
                  src={images?.cloveProfile}
                  alt="cloveProfile"
                  sx={{
                    width: { sm: '90px', md: '100px' },
                    height: { sm: '90px', md: '100px' },
                    objectFit: 'contain'
                  }}
                />
              </Box>
              <Box sx={{ position: 'relative' }}>
                <Typography
                  sx={{
                    color: 'white',
                    fontWeight: 500,
                    mb: 1,
                    fontSize: { sm: '22px', md: '25px' }
                  }}
                >
                  Deadlines. Exam Stress. Career Choices. Loneliness. Relationships.
                </Typography>
                <Typography
                  sx={{
                    color: 'white',
                    fontSize: { sm: '15px', md: '16px' },
                    mb: 2
                  }}
                >
                  College life isn&apos;t always easy. And that&apos;s okay!<br/>
                  We are here to support and be a part of your emotional well-being.
                </Typography>
              </Box>
            </Box>
          </Box>
          
          {/* Decorative leaves - hidden on mobile */}
          <Box
            component="img"
            src={images.leaves}
            alt="leaves"
            sx={{
              display: { xs: 'none', md: 'block' },
              position: 'absolute',
              bottom: '8%',
              right: '2%',
              width: '194px',
              height: '110px',
              objectFit: 'contain',
              pointerEvents: 'none'
            }}
          />
        </Box>

        <Grid container spacing={{ xs: 3, sm: 3.5, md: 4 }}>
          {/* About Clovemind Section */}
          <Grid item xs={12} sm={12} md={7}>
            <Box
              sx={{
                bgcolor: '#fff',
                padding: { xs: "16px", md: "32px" },
                borderRadius: '12px',
                boxShadow: "0 4px 4px 0 rgba(208, 205, 205, 0.10)",
                height: '100%'
              }}
            >
              <Typography
                sx={{
                  color: colors.fontDarkBlue,
                  fontWeight: 600,
                  fontSize: {xs: '16px', sm: '18px', md: '20px'},
                  mb: 0.5,
                }}
              >
                About Clovemind
              </Typography>
              <Typography
                sx={{
                  color: colors.fontGrey,
                  mb: 2.5,
                  fontSize: {xs: '14px', sm: '15px', md: '16px'},
                  lineHeight: 1.6
                }}
              >
                Clovemind is a mental health platform built with students in mind.<br/>
                We focus on holistic emotional and mental wellness, helping you become a stronger, calmer, and more balanced version of yourself.
              </Typography>

              <Box
                sx={{
                  background: "rgba(245, 240, 255, 0.50)",
                  p: 2,
                  borderRadius: '10px',
                  borderLeft: {
                    xs: 'none',
                    sm: '1px solid #F68868', 
                  },
                  borderTop: {
                    xs: '1px solid #F68868', 
                    sm: 'none',
                  },
                }}
              >
                <Typography
                  sx={{
                    color: colors.fontDarkBlue,
                    fontWeight: 600,
                    mb: 1,
                    fontSize: "16px",
                  }}
                >
                  Our Goal 
                </Typography>
                <Typography sx={{
                  fontSize: {xs: '14px', sm: '15px', md: '16px'},
                  fontWeight: 600,
                  color: '#2667B0',
                  mb: 2
                }}>
                  To shift the mindset around well-being:  from waiting until things go wrong to building emotional strength every day. 
                </Typography>
                <Typography
                  sx={{
                    color: colors.fontGrey,
                    mb: 2,
                    lineHeight: 1.6,
                    fontSize: {xs: '14px', sm: '15px', md: '16px'},
                  }}
                >
                  We follow a <Box component="span" sx={{fontWeight: 700}}>person-centered approach</Box>. That means <Box component="span" sx={{fontWeight: 700}}>you</Box> are at the heart of everything we do. With a strong focus on <Box component="span" sx={{fontWeight: 700}}>inclusivity and respect</Box>, every student is welcomed, heard, and empowered. 
                </Typography>
                <Typography
                  sx={{
                    color: colors.fontGrey,
                    lineHeight: 1.6,
                    fontSize: {xs: '14px', sm: '15px', md: '16px'},
                  }}
                >
                  The key to mental well-being? Having someone who would be with you when you need the most
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Why us? Section */}
          <Grid item xs={12} sm={12} md={5}>
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box
                  sx={{
                    width:{ xs: 24, sm: 28, md: 32 },
                    height:{ xs: 24, sm: 28, md: 32 },
                    bgcolor: "#0D9488",
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: '12px'
                  }}
                >
                  <Box
                    component="img"
                    src={images.wellness}
                    alt="wellness"
                    sx={{
                      width: { xs: 18, sm: 21, md: 24 },
                      height: { xs: 18, sm: 21, md: 24 },
                      objectFit: 'contain'
                    }}
                  />
                </Box>

                <Typography
                  sx={{
                    color: colors.fontDarkBlue,
                    fontWeight: 600,
                    fontSize: { xs: '16px', sm: '18px', md: '20px' },
                  }}
                >
                  Why us?
                </Typography>
              </Box>

              <Typography
                sx={{
                  color: colors.fontGrey,
                  mb: 2,
                  lineHeight: '18px',
                  fontSize: { xs: '14px', sm: '15px', md: '16px' },
                  fontWeight: 400,
                }}
              >
                Because we're here to help you feel better, live better, and stay better.
              </Typography>

              {/* Features Grid */}
              <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }} sx={{ flexGrow: 1 }}>
                <Grid item xs={6} >
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center',   
                      justifyContent: 'center',
                      textAlign: 'center', 
                      padding: { xs: "16px", md: "32px" },
                      bgcolor: '#fff',
                      borderRadius: '15px',
                      boxShadow: "0 4px 4px 0 rgba(208, 205, 205, 0.10)",
                      height: '100%'
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: 32, sm: 40, md: 49 },
                        height: { xs: 32, sm: 40, md: 49 },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: { xs: 2, sm: 2.5, md: 3 }
                      }}
                    >
                      <Box
                        component="img"
                        src={images.privacy}
                        alt="privacy"
                        sx={{
                          width: { xs: '32px', sm: '40px', md: '49px' },
                          height: { xs: '32px', sm: '40px', md: '49px' },
                          objectFit: 'contain'
                        }}
                      />
                    </Box>

                    <Typography
                      sx={{
                        color: colors.fontGrey,
                        fontWeight: 500,
                        fontSize: { xs: '12px', sm: '14px', md: '16px' },
                        lineHeight: 1.4
                      }}
                    >
                      100% Private & <br /> Confidential
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} >
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center',   
                      justifyContent: 'center',
                      textAlign: 'center', 
                      padding: { xs: "16px", md: "32px" },
                      bgcolor: '#fff',
                      borderRadius: '15px',
                      boxShadow: "0 4px 4px 0 rgba(208, 205, 205, 0.10)",
                      height: '100%'
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: 32, sm: 40, md: 49 },
                        height: { xs: 32, sm: 40, md: 49 },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: { xs: 2, sm: 2.5, md: 3 }
                      }}
                    >
                      <Box
                        component="img"
                        src={images.price}
                        alt="price"
                        sx={{
                          width: { xs: '32px', sm: '40px', md: '49px' },
                          height: { xs: '32px', sm: '40px', md: '49px' },
                          objectFit: 'contain'
                        }}
                      />
                    </Box>

                    <Typography
                      sx={{
                        color: colors.fontGrey,
                        fontWeight: 500,
                        fontSize: { xs: '12px', sm: '14px', md: '16px' },
                        lineHeight: 1.4
                      }}
                    >
                      Student Friendly & Cost <br/>Effective
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} >
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center',   
                      justifyContent: 'center',
                      textAlign: 'center', 
                      padding: { xs: "16px", md: "32px" },
                      bgcolor: '#fff',
                      borderRadius: '15px',
                      boxShadow: "0 4px 4px 0 rgba(208, 205, 205, 0.10)",
                      height: '100%'
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: 32, sm: 40, md: 49 },
                        height: { xs: 32, sm: 40, md: 49 },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: { xs: 2, sm: 2.5, md: 3 }
                      }}
                    >
                      <Box
                        component="img"
                        src={images.therapy}
                        alt="therapy"
                        sx={{
                          width: { xs: '32px', sm: '40px', md: '49px' },
                          height: { xs: '32px', sm: '40px', md: '49px' },
                          objectFit: 'contain'
                        }}
                      />
                    </Box>

                    <Typography
                      sx={{
                        color: colors.fontGrey,
                        fontWeight: 500,
                        fontSize: { xs: '12px', sm: '14px', md: '16px' },
                        lineHeight: 1.4
                      }}
                    >
                      Multilingual & Professional <br/>Therapists
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} >
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center',   
                      justifyContent: 'center',
                      textAlign: 'center', 
                      padding: { xs: "16px", md: "32px" },
                      bgcolor: '#fff',
                      borderRadius: '15px',
                      boxShadow: "0 4px 4px 0 rgba(208, 205, 205, 0.10)",
                      height: '100%'
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: 32, sm: 40, md: 49 },
                        height: { xs: 32, sm: 40, md: 49 },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: { xs: 2, sm: 2.5, md: 3 }
                      }}
                    >
                      <Box
                        component="img"
                        src={images.schedule}
                        alt="schedule"
                        sx={{
                          width: { xs: '32px', sm: '40px', md: '49px' },
                          height: { xs: '32px', sm: '40px', md: '49px' },
                          objectFit: 'contain'
                        }}
                      />
                    </Box>

                    <Typography
                      sx={{
                        color: colors.fontGrey,
                        fontWeight: 500,
                        fontSize: { xs: '12px', sm: '14px', md: '16px' },
                        lineHeight: 1.4
                      }}
                    >
                      Easy Appointment <br/>Scheduling
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>

        {/* One-on-One Counselling Section */}
        <Box 
          sx={{ 
            mt: { xs: 3, sm: 3.5, md: 4 },
            bgcolor: '#fff',
            padding: { xs: "16px", md: "32px" },
            borderRadius: '16px',
            border:'1px solid #F3F4F6'
          }}
        >
          {/* Tablet Layout: Title and Image Side by Side */}
          <Box sx={{ 
            display: { xs: 'none', sm: 'none', md: 'flex', lg: 'none' },
            alignItems: 'center',
            mb: 3,
            gap: 2
          }}>
            {/* Image */}
            <Box
              sx={{
                width: '120px',
                height: '120px',
                flexShrink: 0
              }}
            >
              <Box
                component="img"
                src={images?.consult}
                alt="consult"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain'
                }}
              />
            </Box>
            {/* Title */}
            <Typography
              sx={{
                color: colors.fontDarkBlue,
                fontWeight: 600,
                fontSize: '20px',
                textTransform: 'uppercase',
              }}
            >
              {servicePurchases[0]?.service_name || "1:1 Virtual Counselling Session"}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row', md: 'column', lg: 'row' },
              justifyContent: { xs: 'flex-start', sm: 'space-between', md: 'flex-start', lg: 'space-between' },
              alignItems: { xs: 'stretch', sm: 'center', md: 'stretch', lg: 'center' },
              gap: 4
            }}
          >
            {/* Mobile Layout - Content in specific order */}
            <Box sx={{ 
              flex: '1 1 auto', 
              maxWidth: { xs: '100%', sm: '50%', md: '100%', lg: '50%' },
              display: 'flex',
              flexDirection: 'column'
            }}>
              {/* Title - hidden on tablet (md), shown on others */}
              <Typography
                sx={{
                  display: { xs: 'block', sm: 'block', md: 'none', lg: 'block' },
                  color: colors.fontDarkBlue,
                  fontWeight: 600,
                  fontSize : {xs: '16px', sm: '18px', md: '20px'},
                  mb: 1,
                  textTransform: 'uppercase',
                  order: { xs: 1, sm: 1 }
                }}
              >
                {servicePurchases[0]?.service_name || "1:1 Virtual Counselling Session"}
              </Typography>
              
              {/* Duration - always second */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: { xs: 2, sm: 3 },
                order: { xs: 2, sm: 2 }
              }}>
                <Box
                  component="img"
                  src={images.clock}
                  alt="clock"
                  sx={{
                    width: {xs:"16px", sm: '20px'},
                    height:{xs:"16px", sm:  '20px'},
                    objectFit: 'contain',
                    mr: 1,
                  }}
                />
                <Typography sx={{ color: "#646262", fontSize: {xs:"14px", sm: '16px'}, fontWeight: 500 }}>
                  60 MINS
                </Typography>
              </Box>
              
              {/* Mobile: Image after duration - hidden on tablet (md) */}
              <Box
                sx={{
                  display: { xs: 'block', sm: 'none', md: 'none' },
                  width: '100%',
                  height: '230px',
                  mb: {xs: 0, sm: 3},
                  order: 3
                }}
              >
                <Box
                  component="img"
                  src={images?.consult}
                  alt="consult"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain'
                  }}
                />
              </Box>
              
              {/* Virtual therapy offers heading */}
              <Typography
                sx={{
                  color: "#111827",
                  fontWeight: 600,
                  mb: 2,
                  fontSize: '16px',
                  order: { xs: 4, sm: 3 }
                }}
              >
                Virtual therapy Offers:
              </Typography>
              
              {/* Bullet points - single column on mobile */}
              <Grid container spacing={2} sx={{ order: { xs: 5, sm: 4 } }}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                    <Box
                      sx={{
                        width: '8px',
                        height: '8px',
                        bgcolor: colors.fontGrey,
                        borderRadius: '50%',
                        mr: 1,
                        flexShrink: 0
                      }}
                    />
                    <Typography sx={{ color: colors.fontGrey, fontSize: {xs:"14px",sm:'16px'} }}>Support</Typography>
                  </Box>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    // mb: { xs: 2, md: 0 }
                  }}>
                    <Box
                      sx={{
                        width: '8px',
                        height: '8px',
                        bgcolor: colors.fontGrey,
                        borderRadius: '50%',
                        mr: 1,
                        flexShrink: 0
                      }}
                    />
                    <Typography sx={{ color: colors.fontGrey, fontSize: {xs:"14px",sm:'16px'} }}>Non-judgmental Space</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                    <Box
                      sx={{
                        width: '8px',
                        height: '8px',
                        bgcolor: colors.fontGrey,
                        borderRadius: '50%',
                        mr: 1,
                        flexShrink: 0
                      }}
                    />
                    <Typography sx={{ color: colors.fontGrey, fontSize: {xs:"14px",sm:'16px'} }}>On par with in-person</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box
                      sx={{
                        width: '8px',
                        height: '8px',
                        bgcolor: colors.fontGrey,
                        borderRadius: '50%',
                        mr: 1,
                        flexShrink: 0
                      }}
                    />
                    <Typography sx={{ color: colors.fontGrey, fontSize: {xs:"14px",sm:'16px'} }}>Multiple therapists</Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* Valid for 30 days - centered on mobile */}
              <Typography
                sx={{
                  color: "#4B5563",
                  fontSize: {xs: "12px", sm:'16px'},
                  mt: 3,
                  mb: {xs:1,sm:2},
                  fontWeight: 500,
                  textAlign: { xs: 'center', sm: 'left' },
                  order: { xs: 6, sm: 5 }
                }}
              >
                Valid for 30 days
              </Typography>

              {/* Book Now button - centered on mobile */}
              <Box sx={{ 
                display: 'flex',
                justifyContent: { xs: 'center', sm: 'flex-start' },
                order: { xs: 7, sm: 6 }
              }}>
                <Button
                  variant="contained"
                  onClick={handleBookNowClick}
                  sx={{
                    height: "34px",
                    fontSize: "14px",
                    fontWeight: 500,
                    px: "24px",
                    py: "8px",
                    borderRadius: "4px",
                    bgcolor: colors.primaryBlue,
                    color: colors.secondary,
                    textTransform: 'none',
                    '&:hover': {
                      bgcolor: colors.primaryBlueDark
                    }
                  }}
                >
                  Book Now
                </Button>
              </Box>
            </Box>
            
            {/* Desktop Illustration - only shown on small desktop and large desktop, hidden on tablet */}
            <Box
              sx={{
                display: { xs: 'none', sm: 'flex', md: 'none', lg: 'flex' },
                flex: '0 0 auto',
                width: { sm: '280px', lg: '550px' },
                height: { sm: '320px', lg: '400px' },
                alignItems: 'center',
                justifyContent: 'center',
                mr: 4
              }}
            >
              <Box
                component="img"
                src={images?.consult}
                alt="consult"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain'
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* Your Purchased Sessions Section - Only show if user has purchases */}
        {hasPurchases && (
          <Box sx={{ my: { xs: 3, sm: 3.5, md: 4 } }}>
            <Accordion
              expanded={purchasedSessionsExpanded}
              onChange={() => {
                const newExpandedState = !purchasedSessionsExpanded;
                setPurchasedSessionsExpanded(newExpandedState);
                clarityEvent("selfcare_purchases_toggled", {
                  userId: userInfo.id,
                  expanded: newExpandedState ? "true" : "false",
                  purchaseCount: servicePurchases.length
                });
              }}
              sx={{
                bgcolor: '#FB85661A',
                borderRadius: '16px !important',
                border: '1px solid #F3F4F6',
                boxShadow: 'none',
                '&:before': {
                  display: 'none',
                },
                '&.Mui-expanded': {
                  margin: '0',
                }
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore sx={{ color: colors.primaryBlue, fontSize: '24px' }} />}
                sx={{
                  minHeight: 'unset',
                  pt: '23px',
                  pb: '16px',
                  px: '23px',
                  '& .MuiAccordionSummary-content': {
                    margin: '0 !important',
                    alignItems: 'center'
                  },
                  '& .MuiAccordionSummary-expandIconWrapper': {
                    marginLeft: 'auto'
                  },
                  '&.Mui-expanded': {
                    minHeight: 'unset',
                  },
                  '& .MuiAccordionSummary-content.Mui-expanded': {
                    margin: '0 !important',
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {/* <Schedule sx={{ fontSize: '20px', color: colors.primaryBlue }} /> */}
                  <Box
                    component="img"
                    src={images.sessions}
                    alt="sessions"
                    sx={{
                      width: {xs: "16px", sm:'18px'},
                      height: {xs: "16px", sm:'18px'},
                      objectFit: 'contain'
                    }}
                  />
                  <Typography
                    sx={{
                      color: colors.fontDarkBlue,
                      fontSize: '16px',
                      fontWeight: 400,
                    }}
                  >
                    Your Purchased Sessions ({servicePurchases.length})
                  </Typography>
                </Box>
              </AccordionSummary>
              
              <AccordionDetails sx={{ p: 0 }}>
                {purchasesLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress size={30} />
                  </Box>
                ) : (
                  <Box sx={{ bgcolor: '#FFFFFF' }}>
                    {servicePurchases.map((purchase, index) => (
                      <Box
                        key={purchase.id}
                        sx={{
                          px: 3,
                          py: 3,
                          borderTop: index === 0 ? '1px solid #E5E7EB' : 'none',
                          borderBottom: index === servicePurchases.length - 1 ? 'none' : '1px solid #E5E7EB',
                          cursor: (purchase.status === 'expired' || purchase.is_expired) ? 'not-allowed' : 'default',
                          opacity: (purchase.status === 'expired' || purchase.is_expired) ? 0.5 : 1,
                          filter: (purchase.status === 'expired' || purchase.is_expired) ? 'grayscale(0.7)' : 'none',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                          <Box sx={{ flex: 1 }}>
                            {/* Session Header with Status */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                              <Typography
                                sx={{
                                  color: (purchase.status === 'expired' || purchase.is_expired) ? '#646262' : colors.fontDarkBlue,
                                  fontSize: {xs: "14px", sm:'18px'},
                                  fontWeight: 600,
                                  textTransform: 'uppercase',
                                }}
                              >
                                {purchase.service_name}
                              </Typography>
                              {purchase.status && (
                                <Box
                                  sx={{
                                    color: purchase.is_expired ? '#494949' : 
                                           purchase.is_active_and_valid ? '#006400' : 
                                           colors.fontGrey,
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    bgcolor: purchase.is_expired ? '#D0CDCD4D' : 
                                            purchase.is_active_and_valid ? '#50C87833' : 
                                            '#F3F4F6',
                                    px: 1.5,
                                    py: 0.5,
                                    borderRadius: '4px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 1.5
                                  }}
                                >
                                  {formatStatus(purchase.status)}
                                  {purchase.status === 'registration_failed' && (
                                    <Tooltip
                                      title="A technical issue was encountered while setting up your service access. Please check your email for further details."
                                      placement="top"
                                      arrow
                                    >
                                      <InfoOutlined 
                                        sx={{ 
                                          fontSize: '16px', 
                                          color: 'currentColor',
                                          cursor: 'pointer'
                                        }} 
                                      />
                                    </Tooltip>
                                  )}
                                </Box>
                              )}
                              {/* {getStatusChip(purchase)} */}
                            </Box>

                            {/* Session Details Row */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                              {/* Duration */}
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Box
                                  component="img"
                                  src={images.clock}
                                  alt="clock"
                                  sx={{
                                    width: '16px',
                                    height: '16px',
                                    objectFit: 'contain',
                                    mr: 0.5,
                                  }}
                                />
                                <Typography sx={{ color: (purchase.status === 'expired' || purchase.is_expired) ? '#646262' : colors.fontLighter2, fontSize: '14px', fontWeight: 500 }}>
                                  {purchase.duration_minutes} MINS
                                </Typography>
                              </Box>

                              {/* Divider */}
                              <Typography sx={{ color: colors.fontGreyLight, fontSize: '14px', display: {xs: 'none', sm: 'inline'} }}>|</Typography>

                              {/* Purchase Date */}
                              <Typography sx={{ color: (purchase.status === 'expired' || purchase.is_expired) ? '#646262' : colors.fontLighter2, fontSize: '14px', fontWeight: 400 }}>
                                Purchased on: <Box component="span" sx={{ fontWeight: 500 }}>{purchase.purchased_date_formatted}</Box>
                              </Typography>

                              {/* Divider */}
                              <Typography sx={{ color: colors.fontGreyLight, fontSize: '14px', display: {xs: 'none', sm: 'inline'}, }}>|</Typography>

                              {/* Expiry Date */}
                              <Typography sx={{ 
                                color: (purchase.status === 'expired' || purchase.is_expired) ? '#646262' : colors.fontLighter2, 
                                fontSize: '14px', 
                                fontWeight: 400 
                              }}>
                                {(purchase.status === 'expired' || purchase.is_expired) ? 'Expired' : 'Expires on'}: <Box component="span" sx={{ fontWeight: 500 }}>{purchase.expires_date_formatted}</Box>
                              </Typography>

                              {/* Divider */}
                              <Typography sx={{ color: colors.fontGreyLight, fontSize: '14px', display: {xs: 'none', sm: 'inline'} }}>|</Typography>

                              {/* Price */}
                              <Typography sx={{ 
                                color: (purchase.status === 'expired' || purchase.is_expired) ? '#646262' : colors.fontLighter2, 
                                fontSize: '14px', 
                                fontWeight: 400 
                              }}>
                                Purchased at: <Box component="span" sx={{ fontWeight: 500 }}>₹{purchase.user_purchase?.total_amount?.toLocaleString() || '0'}/-</Box>
                              </Typography>
                            </Box>
                          </Box>

                          {/* Schedule Button - Only show for first active purchase */}
                          {index === 0 && purchase.is_active_and_valid && !(purchase.status === 'expired' || purchase.is_expired) && (
                            <Button
                              variant="contained"
                              // startIcon={<CalendarToday sx={{ fontSize: '18px' }} />}
                              onClick={() => handleScheduleSession(purchase)}
                              disabled={purchase.status === 'expired' || purchase.is_expired}
                              sx={{
                                height: "34px",
                                fontSize: "14px",
                                fontWeight: 500,
                                px: "24px",
                                py: "8px",
                                borderRadius: "4px",
                                bgcolor: (purchase.status === 'expired' || purchase.is_expired) ? '#646262' : colors.primaryBlue,
                                color: (purchase.status === 'expired' || purchase.is_expired) ? colors.secondary : colors.secondary,
                                textTransform: 'none',
                                opacity: (purchase.status === 'expired' || purchase.is_expired) ? 0.6 : 1,
                                '&:hover': {
                                  bgcolor: (purchase.status === 'expired' || purchase.is_expired) ? '#646262' : colors.primaryBlueDark
                                },
                                '&:disabled': {
                                  bgcolor: '#646262',
                                  color: colors.secondary,
                                  opacity: 0.6
                                }
                              }}
                            >
                              Schedule Session
                            </Button>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          </Box>
        )}
      </Box>
      {/* Consultation Booking Modal */}
        <ConsultationBookingModal
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onScheduleNow={() => {
            // Handle schedule now - redirect to Clovemind platform
            window.open('https://wellbeing.clovemind.com/', '_blank');
            setIsModalOpen(false);
          }}
          onScheduleLater={() => {
            // Handle schedule later - close modal
            console.log('Schedule later clicked');
            setIsModalOpen(false);
          }}
          userEmail={userInfo.email}
          userId={userInfo.id}
        />
    </Box>
  );
};

export function StudentSelfcare() {
  return (
    <StudentNavBar 
      mainComponent={<StudentSelfcareComponent />}
      properties={{ backgroundColor: '#F9FAFB' }}
    />
  );
}